import { apiFetch } from "./client";

/**
 * D-133 Фаза A — Supplier directory API.
 *  - Admin CRUD  (/Custom/admin-center/suppliers)        — admin/QueueAdmin, full fields.
 *  - Manager picker (/Custom/frontend/suppliers)          — read-only, active, БЕЗ notes.
 *  - Auto-proposal (/Custom/frontend/complaints/:id/...)  — default supplier by brand.
 */

// ── shared brand-mapping shapes ──────────────────────────────────────────────
export interface SupplierBrandRef {
  biBrandId: string | null;
  name: string;
  role: string;
  isDefault: boolean;
}

/** Admin view of a mapping (carries the join-row id for editing). */
export interface AdminSupplierBrand extends SupplierBrandRef {
  id: string;
}

// ── admin CRUD ───────────────────────────────────────────────────────────────
export interface AdminSupplierRow {
  id: string;
  name: string;
  email: string | null;
  additionalEmails: string[];
  qualityContactName: string | null;
  qualityContactEmail: string | null;
  salesContactName: string | null;
  salesContactEmail: string | null;
  language: string;
  active: boolean;
  notes: string | null;
  brands: AdminSupplierBrand[];
  createdAt: string | null;
  modifiedAt: string | null;
}

export interface AdminSupplierOptions {
  brands: { biBrandId: string; name: string }[];
  roles: string[];
  languages: string[];
}

export interface AdminSuppliersResponse {
  items: AdminSupplierRow[];
  options: AdminSupplierOptions;
}

export interface SupplierBrandPayload {
  brandBiId: string;
  role: string;
  isDefault: boolean;
}

export interface AdminSupplierPayload {
  name?: string;
  email?: string | null;
  additionalEmails?: string[];
  qualityContactName?: string | null;
  qualityContactEmail?: string | null;
  salesContactName?: string | null;
  salesContactEmail?: string | null;
  language?: string;
  active?: boolean;
  notes?: string | null;
  supplierBrands?: SupplierBrandPayload[];
}

export function fetchAdminSuppliers(signal?: AbortSignal): Promise<AdminSuppliersResponse> {
  return apiFetch<AdminSuppliersResponse>("/Custom/admin-center/suppliers", { signal });
}

export function createSupplier(body: AdminSupplierPayload): Promise<{ id: string; status: string }> {
  return apiFetch("/Custom/admin-center/suppliers", { method: "POST", body });
}

export function updateSupplier(
  id: string,
  body: AdminSupplierPayload,
): Promise<{ id: string; status: string }> {
  return apiFetch(`/Custom/admin-center/suppliers/${encodeURIComponent(id)}`, {
    method: "POST",
    body,
  });
}

export function deleteSupplier(id: string): Promise<{ id: string; status: string }> {
  return apiFetch(`/Custom/admin-center/suppliers/${encodeURIComponent(id)}`, { method: "DELETE" });
}

// ── manager picker (field-private, active-only) ──────────────────────────────
export interface SafeSupplier {
  id: string;
  name: string;
  email: string | null;
  additionalEmails: string[];
  qualityContactName: string | null;
  qualityContactEmail: string | null;
  salesContactName: string | null;
  salesContactEmail: string | null;
  language: string;
  brands: SupplierBrandRef[];
  // 🔴 NO `notes` / `active` by contract (backend SupplierDtoBuilder::safe).
}

export interface SupplierPickerResponse {
  items: SafeSupplier[];
}

export function fetchSupplierPicker(
  opts: { brandBiId?: string; search?: string } = {},
  signal?: AbortSignal,
): Promise<SupplierPickerResponse> {
  const query: Record<string, string> = {};
  if (opts.brandBiId) query.brandBiId = opts.brandBiId;
  if (opts.search) query.search = opts.search;
  return apiFetch<SupplierPickerResponse>("/Custom/frontend/suppliers", { query, signal });
}

// ── auto-proposal by complaint brand (A-W2) ──────────────────────────────────
export interface SupplierSuggestion {
  complaintId: string;
  brand: { biBrandId: string | null; name: string | null; display: string };
  default: SafeSupplier | null;
  suppliers: SafeSupplier[];
  reason: "no_brand" | "no_brand_manual" | "no_mapping_manual" | "mapped_default" | "mapped_no_default";
}

export function fetchSupplierSuggestion(
  complaintId: string,
  signal?: AbortSignal,
): Promise<SupplierSuggestion> {
  return apiFetch<SupplierSuggestion>(
    `/Custom/frontend/complaints/${encodeURIComponent(complaintId)}/supplier-suggestion`,
    { signal },
  );
}

// ── D-133 Фаза C — prepare + send supplier email ─────────────────────────────

/** One re-streamable evidence attachment, with the per-file email-size verdict. */
export interface SupplierPacketAttachment {
  attachmentId: string;
  attachmentType: string;
  fileName: string;
  fileSize: number;
  fileMimeType: string;
  sha256: string;
  /** size ≤ per-file email cap → may be attached (C-W1a). */
  emailable: boolean;
  /** photo/video default-on (C-W2). */
  defaultSelected: boolean;
}

export interface SupplierPacketResponse {
  complaintId: string;
  complaintNumber: string;
  /** audit subject = the complaint number (Gate3). */
  auditSubject: string;
  /** the actual email subject — "Claim COMP-… — <safe desc>". */
  emailSubject: string;
  /** safe Ukrainian body (Phase C; AI translation = Phase D). */
  body: string;
  product: {
    name: string | null;
    sku: string | null;
    brand: string | null;
    brandDisplay: string;
    brandUnverified: boolean;
    batch: string | null;
    batchUnknown: boolean;
    expiryDate: string | null;
    quantity: string | null;
  };
  language: string;
  recipients: {
    brand: { biBrandId: string | null; name: string | null; display: string } | null;
    default: SafeSupplier | null;
    suppliers: SafeSupplier[];
    reason: string | null;
  };
  attachments: SupplierPacketAttachment[];
  limits: {
    individualCapBytes: number;
    totalCapBytes: number;
    individualCapMb: number;
    totalCapMb: number;
  };
  canFreeTextRecipient: boolean;
  canSend: boolean;
}

export interface SupplierSendPayload {
  supplierId: string;
  recipientsTo: string[];
  recipientsCc?: string[];
  attachmentIds: string[];
  freeTextReason?: string;
  language?: string;
  /** D-133 Фаза D — AI draft (що AI запропонував) snapshot для audit (B-W1a). */
  translatedDraftBody?: string;
  /** D-133 Фаза D — тіло, яке людина перевірила/змінила і яке РЕАЛЬНО надсилається. */
  finalBody?: string;
  /** D-133 Фаза D — мова надісланого тіла ('en' = перекладено й перевірено; default 'uk'). */
  bodyLanguage?: "uk" | "en";
}

// ── D-133 Фаза D — AI-переклад тіла (draft, не auto-send) ─────────────────────
export interface SupplierTranslateResult {
  /** 'ok' = переклад є; 'unavailable' = gateway недоступний → ручне редагування (D-W1a). */
  status: "ok" | "unavailable";
  language: string;
  /** оригінал (uk) для відображення поряд із перекладом. */
  originalBody: string;
  /** AI-переклад (en) або '' коли unavailable. */
  translatedBody: string;
  /** типи замаскованого PII перед gateway (напр. ['PHONE','EMAIL']); порожньо = чисто. */
  piiMasked: string[];
}

export function translateSupplierBody(
  complaintId: string,
  body: { targetLang: string },
): Promise<SupplierTranslateResult> {
  return apiFetch<SupplierTranslateResult>(
    `/Custom/frontend/complaints/${encodeURIComponent(complaintId)}/translate-supplier-body`,
    { method: "POST", body },
  );
}

export interface SupplierSendResult {
  ok: boolean;
  duplicate: boolean;
  /** 🔴 smtp_accepted = relay ACCEPTED, NOT delivered/read (C-W1d). */
  status: "smtp_accepted" | "failed" | "sending";
  outboundEmailId: string;
  messageId?: string;
  attachedCount?: number;
  skipped?: { name: string; sizeMb: number }[];
  usedFreeText?: boolean;
  recipients?: { to: string[]; cc: string[] };
}

export function fetchSupplierPacket(
  complaintId: string,
  signal?: AbortSignal,
): Promise<SupplierPacketResponse> {
  return apiFetch<SupplierPacketResponse>(
    `/Custom/frontend/complaints/${encodeURIComponent(complaintId)}/supplier-packet`,
    { signal },
  );
}

export function sendSupplierEmail(
  complaintId: string,
  body: SupplierSendPayload,
): Promise<SupplierSendResult> {
  return apiFetch<SupplierSendResult>(
    `/Custom/frontend/complaints/${encodeURIComponent(complaintId)}/supplier-email`,
    { method: "POST", body },
  );
}

// ── D-133 Фаза E — manual «Додати відповідь постачальника» (E-W2) ────────────
export interface SupplierResponsePayload {
  /** дата відповіді постачальника (YYYY-MM-DD). */
  responseDate: string;
  /** текст відповіді постачальника. */
  text: string;
  /** позиція / результат постачальника (опц.). */
  supplierPosition?: string;
  /** наступна рекомендована дія (опц.). */
  nextAction?: string;
  /** файли постачальника, завантажені під категорією `supplier_response`. */
  attachmentIds?: string[];
}

export interface SupplierResponseResult {
  ok: boolean;
  eventId: string;
  supplierStatus: "replied";
  supplierRepliedAt: string;
  attachmentsCount: number;
}

export function addSupplierResponse(
  complaintId: string,
  body: SupplierResponsePayload,
): Promise<SupplierResponseResult> {
  return apiFetch<SupplierResponseResult>(
    `/Custom/frontend/complaints/${encodeURIComponent(complaintId)}/supplier-response`,
    { method: "POST", body },
  );
}
